import type { SearchResponse } from '@algolia/client-search';
import type { FC, PropsWithChildren } from 'react';

import { useMemo } from 'react';

import type { TileProps } from '~/components/shared/recommendations/tile/Tile';
import type { TileContextProps } from '~/components/shared/recommendations/tile/TileContext';
import { TileProvider } from '~/components/shared/recommendations/tile/TileContext';
import { ALLOWED_SWATCH_IMAGES } from '~/components/shared/recommendations/tile/tileImage/constants';
import type { ProductHit } from '~/types/algolia';
import { useAppContext } from '~/utilities/context/static/AppContext';

export const AlgoliaTileProvider: FC<PropsWithChildren<TileProps>> = ({
  children,
  product,
  variants,
  ...restProps
}) => {
  const { cloudinaryURL1 } = useAppContext();
  const baseImageUrl = `${cloudinaryURL1}c_fill,f_auto,{{dimensions}}/`;

  const transformedVariants = useMemo(
    () =>
      (variants as SearchResponse<ProductHit>['hits'])?.map(variant => {
        const [M07, Z07] = ALLOWED_SWATCH_IMAGES.map(key => variant?.simplifiedImageJson?.[key]);
        const swatchImageUrl = M07?.url ?? Z07?.url;

        return {
          ...variant,
          swatch: swatchImageUrl ? `${baseImageUrl}${swatchImageUrl}` : undefined,
        };
      }),
    [baseImageUrl, variants]
  );

  return (
    <TileProvider
      value={
        {
          product,
          variants: transformedVariants,
          baseImageUrl,
          ...restProps,
        } as TileContextProps
      }
    >
      {children}
    </TileProvider>
  );
};
